<template>
  <div class="flowMeterDetails">
    <VTableLocal
      :key="componentKey"
      :iconHeader="null"
      :items="displayedList"
      :isSearchable="false"
      :isSelectable="false"
      :disablePagination="true"
      :headers="tableHeaders"
      :sortBy="sortBy"
      :sortDesc="sortDesc"
      :showRowIcon="true"
      :itemActions="getItemActions"
      :actionWithLabel="true"
      :centerColumns="['action']"
      immediate
      customClass="deepTable"
      @iconAction="onClickAction"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VTableLocal from '@/components/shared/content-views/VTableLocal';
import Vue from 'vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import FlowMeterForm from './FlowMeterForm.vue';

export default {
    name: 'FlowMeterDetails',
    components: {
        VTableLocal,
    },
    props: {
        meterHub: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            isDataReady: false,
            componentKey: 0,
            sortBy: 'channel',
            sortDesc: false,
            meterChannels: this.meterHub.channelSortedByNumber,
        };
    },


    computed: {
        ...mapGetters([
            'hasPermission',
            'site',
            'teamID',
        ]),
        displayedList() {
            return [...this.meterChannels].sort((a, b) => a.channelNumber - b.channelNumber);
        },

        tableHeaders() {
            return [
                {
                    text: this.$t('devices.channel'),
                    value: 'channel',
                    sortable: false,
                },
                {
                    text: this.$t('devices.name'),
                    value: 'name',
                    sortable: false,
                },
                {
                    text: this.$t('devices.measureUnit'),
                    value: 'unitsOfMeasure',
                    sortable: false,
                },

                {
                    text: this.$t('devices.volumePerPulse'),
                    value: 'volumePerPulse',
                    sortable: false,
                },
                {
                    text: this.$t('forms.labels.network'),
                    value: 'network',
                    sortable: false,
                },
                {
                    text: this.$t('devices.component'),
                    value: 'nodeDetails.name',
                    sortable: false,
                },
            ];
        },

    },
    watch: {
        meterChannels: {
            handler() {
                this.componentKey += 1;
            },
            deep: true,
            immediate: true,
        },
    },

    mounted() {
        this.prepareData();
    },

    methods: {
        async prepareData() {
            await this.$nextTick();
            this.isDataReady = true;
        },
        getItemActions(item) {
            const actions = [];
            if (this.hasPermission('controllers.edit')) {
                actions.push({
                    label: this.$t('devices.flowMeters.edit'),
                    icon: 'edit',
                    action: 'edit',
                    condition: item.id !== null && item.id !== undefined,
                });
            }
            if (this.hasPermission('controllers.add')) {
                actions.push({
                    label: this.$t('devices.flowMeters.add'),
                    icon: 'mdi-link-variant-plus',
                    action: 'link',
                    condition: item.id === null || item.id === undefined,
                });
            }
            if (this.hasPermission('controllers.remove')) {
                actions.push({
                    label: this.$t('devices.flowMeters.remove'),
                    icon: 'delete',
                    action: 'delete',
                    condition: item.id !== null && item.id !== undefined,
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions.filter(action => action.condition);
        },

        async onClickAction(item, action) {
            switch (action) {
                case 'delete':
                    await this.deleteFlowMeter(item);
                    break;
                case 'edit':
                    await this.openUpdateOneFlowMeterForm(item);
                    break;
                case 'link':
                    await this.linkFlowMeter(item);
                    break;
                default:
                    break;
            }
        },

        async linkFlowMeter(meter) {
            const position = meter.channelNumber - 1;
            let newPosition;
            const updatedHubConfiguration = this.meterHub;
            const { maxIndirectChannels } = this.meterHub;
            let channel;
            if (position < maxIndirectChannels) {
                channel = 'indirectChannels';
                newPosition = position;
                // If the position is within the controller channel range, add it there
                // Ensure the array is large enough
                updatedHubConfiguration.indirectChannels[position] = meter;
            } else {
                channel = 'directChannels';
                // Otherwise, add it to the direct channel
                // Adjust position for the direct channel array
                const directPosition = position - maxIndirectChannels;
                newPosition = directPosition;
                updatedHubConfiguration.directChannels[directPosition] = meter;
            }

            this.$root.$emit('showModal', {
                title: this.$t('devices.flowMeters.add'),
                component: Vue.component('FlowMeterForm', FlowMeterForm),
                payload: { meter, dataSource: 'flowMeterHub' },
                onConfirm: async payload => {
                    updatedHubConfiguration[channel][newPosition] = payload;
                    const response = await this.$rq.updateMeterHubConfiguration(this.site.id, this.meterHub.id, updatedHubConfiguration);
                    const newMeterId = response.data[channel][newPosition];
                    const newMeter = {
                        ...payload,
                        id: newMeterId,
                    };
                    this.$set(this.meterHub.channelSortedByNumber, position, newMeter);
                },
            });
        },

        async deleteFlowMeter(meter) {
            this.$root.$emit('showModal', {
                title: this.$t('devices.flowMeters.remove'),
                meterName: meter?.name,
                component: Vue.component('confirmRemove', ConfirmForm),
                payload: { msg: this.$t('devices.flowMeters.removeMsg'), removeMsgSecond: this.$t('devices.flowMeters.removeMsgSecond') },
                onConfirm: async () => {
                    await this.$rq.unlinkAndDeleteFlowMeter(this.site.id, this.meterHub.id, meter.id);
                    const position = meter.channelNumber - 1;
                    const emptyMeter = {
                        channel: meter.channel,
                        channelNumber: meter.channelNumber,
                        name: '',
                        resetAt: 65000,
                        type: 'IOT',
                        unitsOfMeasure: '',
                        volumePerPulse: 0,
                        component: '',
                        id: null,
                    };
                    this.$set(this.meterHub.channelSortedByNumber, position, emptyMeter);
                },
                isForm: false,
            });
        },

        async openUpdateOneFlowMeterForm(meter) {
            this.$root.$emit('showModal', {
                title: this.$t('devices.flowMeters.edit'),
                component: Vue.component('FlowMeterForm', FlowMeterForm),
                payload: { meter },
                onConfirm: async payload => {
                    // Create a new object, setting 'team' and 'site' if they're not present
                    const updatedMeter = {
                        ...payload,
                        team: payload.team || this.teamID,
                        site: payload.site || this.site.id,
                        volumePerPulse: parseFloat(payload.volumePerPulse),
                    };

                    await this.$rq.updateFlowMeter(updatedMeter.site, updatedMeter.id, updatedMeter);
                    const position = meter.channelNumber - 1;
                    this.$set(this.meterHub.channelSortedByNumber, position, payload);
                },
            });
        },

    },
};
</script>
<style lang="scss">
    .flowMeterDetails td {
        width: 350px
    }
    @media screen and (max-width: 600px) {
        .flowMeterDetails td {
            width: max-content
        }
    }
</style>
